import { useLoaderData } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight, Database, Download, Rows3, Search, Table2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

interface TableSummary {
  name: string;
  rowCount: number;
}

interface TableData {
  columns: string[];
  rows: Record<string, unknown>[];
  totalRows: number;
  page: number;
  pageSize: number;
}

export function DatabaseBrowserPage() {
  const tables = useLoaderData({
    from: "/_authenticatedFullBleed/database-browser",
  }) as TableSummary[];
  const [selected, setSelected] = useState<string>(tables[0]?.name ?? "");
  const [data, setData] = useState<TableData | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [tableFilter, setTableFilter] = useState("");
  const [dataSearch, setDataSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const tableRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);

  const filteredTables = useMemo(() => {
    if (!tableFilter) return tables;
    const q = tableFilter.toLowerCase();
    return tables.filter((t) => t.name.toLowerCase().includes(q));
  }, [tables, tableFilter]);

  const totalRows = useMemo(() => tables.reduce((s, t) => s + t.rowCount, 0), [tables]);

  // Debounce the data search input by 300ms
  function handleDataSearchChange(value: string) {
    setDataSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
  }

  const fetchData = useCallback(
    async (tableName: string, p: number, ps: number, searchTerm: string) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(p),
          pageSize: String(ps),
        });
        if (searchTerm) params.set("search", searchTerm);
        const res = await fetch(`/api/schema/${tableName}/data?${params}`);
        if (res.ok) {
          setData(await res.json());
        }
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (selected) {
      fetchData(selected, page, pageSize, debouncedSearch);
    }
  }, [selected, page, pageSize, debouncedSearch, fetchData]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll on table/page change
  useEffect(() => {
    tableRef.current?.scrollTo(0, 0);
  }, [selected, page]);

  const totalPages = data ? Math.ceil(data.totalRows / pageSize) : 0;
  const rangeStart = data ? (page - 1) * pageSize + 1 : 0;
  const rangeEnd = data ? Math.min(page * pageSize, data.totalRows) : 0;

  function selectTable(name: string) {
    setSelected(name);
    setPage(1);
    setData(null);
    setDataSearch("");
    setDebouncedSearch("");
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }

  return (
    <div className="flex h-full overflow-hidden bg-[oklch(0.97_0.005_240)]">
      {/* ── Left Panel: Table Navigator ── */}
      <div className="w-72 shrink-0 flex flex-col border-r border-[oklch(0.88_0.015_240)] bg-[oklch(0.985_0.003_240)]">
        {/* Header */}
        <div className="px-5 pt-5 pb-3">
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-8 h-8 rounded-lg bg-brand-navy flex items-center justify-center">
              <Database className="w-4 h-4 text-brand-gold" />
            </div>
            <div>
              <h1 className="font-serif text-base text-brand-navy leading-tight">Database</h1>
              <p className="text-[11px] text-muted-foreground">
                {tables.length} tables &middot; {totalRows.toLocaleString()} rows
              </p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="px-3 pb-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/60" />
            <input
              type="text"
              placeholder="Filter tables..."
              value={tableFilter}
              onChange={(e) => setTableFilter(e.target.value)}
              className="w-full h-8 pl-8 pr-3 text-xs rounded-md border border-[oklch(0.88_0.015_240)] bg-white/60 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-brand-gold/40 focus:border-brand-gold/40 transition-colors"
            />
          </div>
        </div>

        {/* Table List */}
        <div className="flex-1 overflow-y-auto px-2 pb-3">
          {filteredTables.map((t) => {
            const isActive = selected === t.name;
            return (
              <button
                key={t.name}
                type="button"
                onClick={() => selectTable(t.name)}
                className={cn(
                  "w-full text-left px-3 py-2 rounded-md flex items-center gap-2.5 group transition-all duration-150",
                  isActive
                    ? "bg-brand-navy text-white shadow-sm"
                    : "hover:bg-[oklch(0.94_0.01_240)] text-foreground",
                )}
              >
                <Table2
                  className={cn(
                    "w-3.5 h-3.5 shrink-0 transition-colors",
                    isActive
                      ? "text-brand-gold"
                      : "text-muted-foreground/50 group-hover:text-muted-foreground",
                  )}
                />
                <span className="font-mono text-[12px] truncate flex-1 tracking-tight">
                  {t.name}
                </span>
                <span
                  className={cn(
                    "font-mono text-[10px] tabular-nums shrink-0 transition-colors",
                    isActive ? "text-white/60" : "text-muted-foreground/50",
                  )}
                >
                  {t.rowCount.toLocaleString()}
                </span>
              </button>
            );
          })}
          {filteredTables.length === 0 && (
            <div className="px-3 py-6 text-center">
              <p className="text-xs text-muted-foreground/60">No tables match</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Right Panel: Data View ── */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Toolbar */}
        <div className="h-12 shrink-0 flex items-center justify-between px-4 border-b border-[oklch(0.88_0.015_240)] bg-[oklch(0.985_0.003_240)]">
          <div className="flex items-center gap-3 min-w-0">
            <h2 className="font-mono text-sm font-semibold text-brand-navy truncate">{selected}</h2>
            {data && (
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground bg-[oklch(0.94_0.01_240)] px-2 py-0.5 rounded-full">
                <Rows3 className="w-3 h-3" />
                {data.totalRows.toLocaleString()} rows
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {selected && (
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/50" />
                <input
                  type="text"
                  placeholder="Search all rows..."
                  value={dataSearch}
                  onChange={(e) => handleDataSearchChange(e.target.value)}
                  className="w-56 h-7 pl-8 pr-3 text-xs font-mono rounded-md border border-[oklch(0.88_0.015_240)] bg-white/60 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-brand-gold/40 focus:border-brand-gold/40 transition-colors"
                />
              </div>
            )}
            {selected && (
              <a href={`/api/schema/${selected}/csv`} download>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs gap-1.5 border-[oklch(0.85_0.02_70)] text-brand-navy hover:bg-brand-gold/10 hover:border-brand-gold/40 hover:text-brand-navy"
                >
                  <Download className="w-3.5 h-3.5" />
                  Export CSV
                </Button>
              </a>
            )}
          </div>
        </div>

        {/* Data Grid */}
        <div ref={tableRef} className="flex-1 overflow-auto">
          {loading && !data && (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-3 animate-fade-up">
                <div className="w-10 h-10 rounded-xl bg-brand-navy/5 flex items-center justify-center">
                  <Database className="w-5 h-5 text-brand-navy/30 animate-pulse" />
                </div>
                <p className="text-xs text-muted-foreground">Loading data...</p>
              </div>
            </div>
          )}
          {data && (
            <table className="w-full text-left border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className="bg-[oklch(0.22_0.05_240)] text-[oklch(0.75_0.02_70)]">
                  <th className="px-3 py-2 text-[10px] uppercase tracking-wider font-semibold border-b border-[oklch(0.30_0.04_240)] text-center w-12 text-[oklch(0.55_0.02_240)]">
                    #
                  </th>
                  {data.columns.map((col) => (
                    <th
                      key={col}
                      className="px-3 py-2 text-[10px] uppercase tracking-wider font-semibold border-b border-[oklch(0.30_0.04_240)] whitespace-nowrap"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, i) => {
                  const rowNum = rangeStart + i;
                  return (
                    <tr
                      key={`${page}-${rowNum}`}
                      className={cn(
                        "border-b border-[oklch(0.92_0.005_240)] transition-colors hover:bg-brand-gold/[0.04]",
                        i % 2 === 0 ? "bg-white" : "bg-[oklch(0.975_0.003_240)]",
                      )}
                    >
                      <td className="px-3 py-1.5 text-center font-mono text-[10px] text-muted-foreground/40 tabular-nums">
                        {rowNum}
                      </td>
                      {data.columns.map((col) => (
                        <td
                          key={col}
                          className="px-3 py-1.5 font-mono text-[12px] whitespace-nowrap max-w-[320px] truncate text-foreground/80"
                          title={row[col] != null ? String(row[col]) : ""}
                        >
                          {row[col] != null ? (
                            String(row[col])
                          ) : (
                            <span className="text-muted-foreground/30 italic text-[11px]">
                              null
                            </span>
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {data.rows.length === 0 && (
                  <tr>
                    <td colSpan={data.columns.length + 1} className="py-16 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <Table2 className="w-8 h-8 text-muted-foreground/20" />
                        <p className="text-sm text-muted-foreground/50">Empty table</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination Footer */}
        {data && data.totalRows > 0 && (
          <div className="h-10 shrink-0 flex items-center justify-between px-4 border-t border-[oklch(0.88_0.015_240)] bg-[oklch(0.985_0.003_240)]">
            <div className="flex items-center gap-3">
              <span className="text-[11px] text-muted-foreground">Rows per page</span>
              <Select
                value={String(pageSize)}
                onValueChange={(v) => {
                  setPageSize(Number(v));
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-16 h-6 text-[11px] border-[oklch(0.88_0.015_240)]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="250">250</SelectItem>
                  <SelectItem value="500">500</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[11px] text-muted-foreground tabular-nums">
                {rangeStart}&ndash;{rangeEnd} of {data.totalRows.toLocaleString()}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
